/**
 * Custom Cursor - Premium interactive cursor
 *
 * @package Michela_Franceschina
 * @since 1.5.0
 */


(function () {
    const CustomCursor = {
        cursor: null,
        cursorInner: null,
        cursorText: null,
        controller: null,
        rafId: null,
        mouseX: 0,
        mouseY: 0,
        cursorX: 0,
        cursorY: 0,
        scale: 1,
        isVisible: false,

        init() {
            // Skip on touch / coarse pointer devices (covers phones + iPad in finger mode)
            if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
                return;
            }
            if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
                return;
            }

            if (this.controller) this.controller.abort();
            this.controller = new AbortController();

            this.createCursor();
            this.bindEvents();
            this.animate();
        },

        destroy() {
            if (this.controller) {
                this.controller.abort();
                this.controller = null;
            }
            if (this.rafId) {
                cancelAnimationFrame(this.rafId);
                this.rafId = null;
            }
        },

        createCursor() {
            // Main cursor (outer ring)
            this.cursor = document.createElement('div');
            this.cursor.className = 'custom-cursor';
            this.cursor.id = 'custom-cursor';

            // Text label inside outer cursor (shown on data-cursor-text targets)
            this.cursorText = document.createElement('span');
            this.cursorText.className = 'custom-cursor-text';
            this.cursor.appendChild(this.cursorText);

            document.body.appendChild(this.cursor);

            // Inner dot
            this.cursorInner = document.createElement('div');
            this.cursorInner.className = 'custom-cursor-inner';
            this.cursorInner.id = 'custom-cursor-inner';
            document.body.appendChild(this.cursorInner);

            const style = document.createElement('style');
            style.textContent = `
                .custom-cursor {
                    position: fixed;
                    top: 0;
                    left: 0;
                    pointer-events: none;
                    z-index: 99999;
                    will-change: transform;
                }
                .custom-cursor-inner {
                    position: fixed;
                    top: 0;
                    left: 0;
                    width: 6px;
                    height: 6px;
                    background: var(--color-accent, #d4af37);
                    border-radius: 50%;
                    pointer-events: none;
                    z-index: 100000;
                    transition: width 0.2s ease, height 0.2s ease, background 0.2s ease;
                    will-change: transform;
                }
                .custom-cursor-inner.is-clicking {
                    width: 4px;
                    height: 4px;
                }
                .custom-cursor-inner.is-hovering {
                    width: 10px;
                    height: 10px;
                    background: var(--color-primary, #1a4d5e);
                }
                .custom-cursor.is-hidden,
                .custom-cursor-inner.is-hidden {
                    opacity: 0;
                }
                .custom-cursor-text {
                    position: absolute;
                    top: 50%;
                    left: 50%;
                    transform: translate(-50%, -50%);
                    font-size: 0.75rem;
                    font-weight: 600;
                    text-transform: uppercase;
                    letter-spacing: 0.05em;
                    color: var(--color-bg-white, #fff);
                    opacity: 0;
                    white-space: nowrap;
                    transition: opacity 0.2s ease;
                    pointer-events: none;
                }
                .custom-cursor.is-expanded .custom-cursor-text {
                    opacity: 1;
                }
            `;
            document.head.appendChild(style);

            document.body.classList.add('has-custom-cursor');
        },

        bindEvents() {
            const sig = { signal: this.controller.signal };

            document.addEventListener('mousemove', (e) => {
                this.mouseX = e.clientX;
                this.mouseY = e.clientY;

                if (!this.isVisible) {
                    this.cursor.style.opacity = '1';
                    this.cursorInner.style.opacity = '1';
                    this.isVisible = true;
                }
            }, sig);

            document.addEventListener('mouseleave', () => {
                this.cursor.classList.add('is-hidden');
                this.cursorInner.classList.add('is-hidden');
                this.isVisible = false;
            }, sig);

            document.addEventListener('mouseenter', () => {
                this.cursor.classList.remove('is-hidden');
                this.cursorInner.classList.remove('is-hidden');
                this.isVisible = true;
            }, sig);

            document.addEventListener('mousedown', () => {
                this.scale = 0.8;
                this.cursorInner.classList.add('is-clicking');
            }, sig);

            document.addEventListener('mouseup', () => {
                this.scale = 1;
                this.cursorInner.classList.remove('is-clicking');
            }, sig);

            // Event delegation: single set of listeners on document instead of N per element.
            // Saves N×2 listeners and survives DOM mutations (filter AJAX re-renders).
            document.addEventListener('mouseover', (e) => {
                const t = e.target;
                if (!(t instanceof Element)) return;

                if (t.matches('a, button, .card, input, textarea, [role="button"]')) {
                    this.cursor.classList.add('is-hovering');
                    this.cursorInner.classList.add('is-hovering');
                }
                if (t.matches('img, .card-image')) {
                    this.cursor.style.mixBlendMode = 'difference';
                }
                const textEl = t.closest('[data-cursor-text]');
                if (textEl) {
                    this.cursorText.textContent = textEl.getAttribute('data-cursor-text') || '';
                    this.cursor.classList.add('is-expanded');
                }
            }, sig);

            document.addEventListener('mouseout', (e) => {
                const t = e.target;
                if (!(t instanceof Element)) return;

                if (t.matches('a, button, .card, input, textarea, [role="button"]')) {
                    this.cursor.classList.remove('is-hovering');
                    this.cursorInner.classList.remove('is-hovering');
                }
                if (t.matches('img, .card-image')) {
                    this.cursor.style.mixBlendMode = 'normal';
                }
                if (t.closest('[data-cursor-text]')) {
                    this.cursor.classList.remove('is-expanded');
                }
            }, sig);
        },

        animate() {
            const lerp = 0.15;
            this.cursorX += (this.mouseX - this.cursorX) * lerp;
            this.cursorY += (this.mouseY - this.cursorY) * lerp;

            // GPU-composited transform — no layout thrashing
            this.cursor.style.transform = `translate3d(${this.cursorX}px, ${this.cursorY}px, 0) translate(-50%, -50%) scale(${this.scale})`;
            this.cursorInner.style.transform = `translate3d(${this.mouseX}px, ${this.mouseY}px, 0) translate(-50%, -50%)`;

            this.rafId = requestAnimationFrame(() => this.animate());
        }
    };

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => CustomCursor.init());
    } else {
        CustomCursor.init();
    }

    window.CustomCursor = CustomCursor;

})();

export {};
